/**
 * "What we do" — the programme areas the NGO delivers against, each an image-led card
 * with a lucide icon, short copy and an outcome line. Content-rich and scannable; every
 * programme travels the same verified, dated proof chain on the Served wall.
 */
import { Construction, Bath, Stethoscope, GraduationCap, HeartHandshake, Baby, LifeBuoy, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useReveal } from '../hooks/useReveal.js';
import { Figure } from '../components/ui/Figure.jsx';
import styles from './Programs.module.css';

const PROGRAMS = [
  {
    icon: Construction,
    img: '/images/room-construction.jpg',
    title: 'Room Construction',
    body: 'A pucca room built for families living in unsafe kachcha shelters — solid walls and a dry roof, finished before the next monsoon, not after.',
    outcome: 'A solid roof overhead',
  },
  {
    icon: Bath,
    img: '/images/bathroom-construction.jpg',
    title: 'Bathroom Construction',
    body: 'A private, hygienic toilet and bathing space built at the home — safety, dignity and sanitation, especially for women and the elderly.',
    outcome: 'Dignity & sanitation',
  },
  {
    icon: Stethoscope,
    img: '/images/medical-assistance.jpg',
    title: 'Medical Assistance',
    body: 'Hospital bills, medicines, surgeries and ambulance costs settled directly with the provider for families facing a sudden health crisis.',
    outcome: 'Treatment, on time',
  },
  {
    icon: GraduationCap,
    img: '/images/education-scholarship.jpg',
    title: 'Education Scholarship',
    body: 'School and college fees, books, uniforms and exam costs so a bright student stays in class instead of dropping out to earn.',
    outcome: 'Students who stay in school',
  },
  {
    icon: HeartHandshake,
    img: '/images/wedding.jpg',
    title: 'Wedding (Only for Girls)',
    body: 'Respectful support towards the marriage of girls from families who cannot meet the cost — given quietly and with dignity, never as charity paraded.',
    outcome: 'A dignified start',
  },
  {
    icon: Baby,
    img: '/images/infants-welfare.jpg',
    title: 'Infants Welfare',
    body: 'Nutrition, medicine and early care for newborns and infants in vulnerable households — a healthy start through the first, most fragile years.',
    outcome: 'Healthy beginnings',
  },
  {
    icon: LifeBuoy,
    img: '/images/flood-disaster-relief.jpg',
    title: 'Flood & Disaster Relief',
    body: 'Rapid relief when disaster strikes — dry rations, clean water, bedding and rebuilding support for families hit by floods and other emergencies.',
    outcome: 'Help when it matters most',
  },
];

export function Programs() {
  const ref = useReveal({ threshold: 0.1 });
  return (
    <section id="programs" className={styles.section}>
      <div className="container">
        <div className={styles.head}>
          <div>
            <p className="eyebrow">What we do</p>
            <h2 className={styles.title}>Seven ways help reaches the ground.</h2>
          </div>
          <p className={styles.intro}>
            Every programme runs through the same proof chain — verified intake, ground
            verification, tiered approval and a delivery acknowledgement. The category changes;
            the standard of evidence never does.
          </p>
        </div>

        <div ref={ref} className={styles.grid} data-revealed="false">
          {PROGRAMS.map((p, i) => (
            <article key={p.title} className={styles.card} style={{ '--i': i }}>
              <div className={styles.media}>
                <Figure id={p.img} alt={p.title} ratio="16 / 10" w={640} />
                <span className={styles.iconBadge}>
                  <p.icon size={20} aria-hidden="true" />
                </span>
              </div>
              <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>{p.title}</h3>
                <p className={styles.cardCopy}>{p.body}</p>
                <span className={styles.outcome}>{p.outcome}</span>
              </div>
            </article>
          ))}
        </div>

        <div className={styles.foot}>
          <Link to="/served" className={styles.footLink}>
            See real cases on the Served wall <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default Programs;
